import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, TextField, Typography } from "@material-ui/core";
import type { PregnancyWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("pregnancy");

const VIEW_OPTIONS = [
  { key: "transabdominalSagittal", label: "TA Sagittal" },
  { key: "transabdominalTransverse", label: "TA Transverse" },
  { key: "transvaginalSagittal", label: "TV Sagittal" },
  { key: "transvaginalTransverse", label: "TV Transverse" },
] as const;

const CARDIAC_ACTIVITY_OPTIONS = [
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "not_assessed", label: "Not Assessed" },
] as const;

const PRESENTATION_OPTIONS = [
  { value: "cephalic", label: "Cephalic" },
  { value: "breech", label: "Breech" },
  { value: "transverse", label: "Transverse" },
  { value: "oblique", label: "Oblique" },
  { value: "unknown", label: "Unknown" },
] as const;

const FREE_FLUID_OPTIONS = [
  { value: "none", label: "None" },
  { value: "small", label: "Small" },
  { value: "moderate", label: "Moderate" },
  { value: "large", label: "Large" },
] as const;

// All dating math operates in UTC to keep derived dates stable regardless of
// the clinician's local timezone. LMP/EDD are stored as date-only strings
// (YYYY-MM-DD) and interpreted as UTC midnight throughout.

const todayUtcMs = (): number => {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

const formatUtcDate = (ms: number): string =>
  new Date(ms).toISOString().slice(0, 10);

// Naegele's rule — LMP + 280 days. Returns ISO date string (YYYY-MM-DD).
const computeEddFromLmp = (lmpDate: string): string | null => {
  const lmp = new Date(`${lmpDate}T00:00:00Z`);
  if (Number.isNaN(lmp.getTime())) return null;
  return formatUtcDate(lmp.getTime() + 280 * 24 * 60 * 60 * 1000);
};

const computeGaWeeksFromLmp = (lmpDate: string): number | null => {
  const lmp = new Date(`${lmpDate}T00:00:00Z`);
  if (Number.isNaN(lmp.getTime())) return null;
  const days = (todayUtcMs() - lmp.getTime()) / (24 * 60 * 60 * 1000);
  if (days < 0) return null;
  return Number((days / 7).toFixed(1));
};

// Robinson-Fleming CRL → GA (weeks). Valid for CRL 5–84 mm — outside this
// range the formula extrapolates poorly, so we return null and the clinician
// falls back to LMP-based dating.
// GA(days) = 8.052 * sqrt(CRL_mm) + 23.73
const computeGaWeeksFromCrl = (crlMm: number): number | null => {
  if (!Number.isFinite(crlMm) || crlMm < 5 || crlMm > 84) return null;
  const days = 8.052 * Math.sqrt(crlMm) + 23.73;
  return Number((days / 7).toFixed(1));
};

const computeEddFromGaWeeks = (gaWeeks: number): string | null => {
  if (!Number.isFinite(gaWeeks) || gaWeeks <= 0) return null;
  const remainingDays = (40 - gaWeeks) * 7;
  return formatUtcDate(todayUtcMs() + remainingDays * 24 * 60 * 60 * 1000);
};

// numberOfGestations is collected via a numeric text input which can yield
// NaN, 0, or non-integer values. Normalize to a finite positive integer so
// downstream checks (isPregnancyComplete, CPT units) don't propagate junk.
export const isValidGestationCount = (n: number | null): n is number =>
  n !== null && Number.isFinite(n) && Number.isInteger(n) && n >= 1;

// Hadlock IV (1985) — log10(EFW_g) from BPD/HC/AC/FL (all converted to cm).
// log10(EFW) = 1.3596 + 0.0064*HC + 0.0424*AC + 0.174*FL
//                     + 0.00061*BPD*AC - 0.00386*AC*FL
// Requires all four measurements; returns null otherwise.
export const computeEfwGrams = (
  bpdMm: number | null,
  hcMm: number | null,
  acMm: number | null,
  flMm: number | null,
): number | null => {
  if (
    bpdMm === null ||
    hcMm === null ||
    acMm === null ||
    flMm === null ||
    !Number.isFinite(bpdMm) ||
    !Number.isFinite(hcMm) ||
    !Number.isFinite(acMm) ||
    !Number.isFinite(flMm)
  ) {
    return null;
  }
  const bpd = bpdMm / 10;
  const hc = hcMm / 10;
  const ac = acMm / 10;
  const fl = flMm / 10;
  const log10Efw =
    1.3596 +
    0.0064 * hc +
    0.0424 * ac +
    0.174 * fl +
    0.00061 * bpd * ac -
    0.00386 * ac * fl;
  const grams = 10 ** log10Efw;
  if (!Number.isFinite(grams) || grams <= 0 || grams > 10_000) return null;
  return Math.round(grams);
};

export const isPregnancyComplete = (data: PregnancyWorksheetData): boolean => {
  const f = data.findings;
  if (f.intrauterineGestation === null) return false;
  // For a no-IUP scan (suspected ectopic, very early loss) there is no fetus,
  // so don't gate on cardiac activity / presentation / gestation count.
  if (f.intrauterineGestation === false) return true;
  return (
    f.cardiacActivity !== null &&
    f.fetalPresentation !== null &&
    isValidGestationCount(f.numberOfGestations)
  );
};

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const DateInput: FC<{
  value: string | null;
  onChange: (v: string | null) => void;
  label: string;
  disabled?: boolean;
}> = ({ value, onChange, label, disabled }) => (
  <TextField
    type="date"
    value={value ?? ""}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value || null)
    }
    variant="outlined"
    size="small"
    label={label}
    disabled={disabled}
    InputLabelProps={{ shrink: true }}
    inputProps={{ "aria-label": label }}
    style={{ maxWidth: 200 }}
  />
);

export const PregnancyWorksheet: FC<{
  data: PregnancyWorksheetData;
  onChange: (data: PregnancyWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateFindings = (
    patch: Partial<PregnancyWorksheetData["findings"]>,
  ) => {
    onChange({ ...data, findings: { ...data.findings, ...patch } });
  };

  // Best-guess GA: prefer CRL when in Robinson-Fleming range, otherwise LMP.
  // Mirrors the precedence in handleCrlChange so the two handlers stay symmetric:
  // recompute from LMP unless CRL is already producing a valid Robinson-Fleming GA.
  const handleLmpChange = (lmp: string | null) => {
    const patch: Partial<PregnancyWorksheetData["findings"]> = { lmpDate: lmp };
    const currentCrl = data.findings.crownRumpLengthMm;
    const crlGa =
      currentCrl !== null ? computeGaWeeksFromCrl(currentCrl) : null;
    if (crlGa === null) {
      // CRL absent or out of range — LMP is now the source of truth.
      patch.estimatedGestationalAgeWeeks = lmp
        ? computeGaWeeksFromLmp(lmp)
        : null;
      patch.estimatedDueDate = lmp ? computeEddFromLmp(lmp) : null;
    }
    updateFindings(patch);
  };

  const handleCrlChange = (crl: number | null) => {
    const patch: Partial<PregnancyWorksheetData["findings"]> = {
      crownRumpLengthMm: crl,
    };
    const crlGa = crl !== null ? computeGaWeeksFromCrl(crl) : null;
    if (crlGa !== null) {
      patch.estimatedGestationalAgeWeeks = crlGa;
      patch.estimatedDueDate = computeEddFromGaWeeks(crlGa);
    } else if (data.findings.lmpDate) {
      // CRL absent or outside Robinson-Fleming validity — fall back to LMP
      // rather than wiping previously-derived dating values.
      patch.estimatedGestationalAgeWeeks = computeGaWeeksFromLmp(
        data.findings.lmpDate,
      );
      patch.estimatedDueDate = computeEddFromLmp(data.findings.lmpDate);
    } else {
      patch.estimatedGestationalAgeWeeks = null;
      patch.estimatedDueDate = null;
    }
    updateFindings(patch);
  };

  // Recompute EFW (Hadlock IV) whenever any of BPD/HC/AC/FL changes.
  const handleBiometryChange = (
    key:
      | "biparietalDiameterMm"
      | "headCircumferenceMm"
      | "abdominalCircumferenceMm"
      | "femurLengthMm",
    value: number | null,
  ) => {
    const next = { ...data.findings, [key]: value };
    next.estimatedFetalWeightG = computeEfwGrams(
      next.biparietalDiameterMm,
      next.headCircumferenceMm,
      next.abdominalCircumferenceMm,
      next.femurLengthMm,
    );
    onChange({ ...data, findings: next });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as PregnancyWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Dates">
            <DateInput
              value={data.findings.lmpDate}
              onChange={handleLmpChange}
              label="LMP"
              disabled={readOnly}
            />
            <FindingLabel>Estimated GA</FindingLabel>
            <NumericInput
              value={data.findings.estimatedGestationalAgeWeeks}
              onChange={(v) =>
                updateFindings({ estimatedGestationalAgeWeeks: v })
              }
              unit="wk"
              step={0.1}
              label="EGA"
              disabled={readOnly}
            />
            <DateInput
              value={data.findings.estimatedDueDate}
              onChange={(v) => updateFindings({ estimatedDueDate: v })}
              label="EDD"
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <WorksheetSection label="Fetal Findings">
            <FindingLabel>Intrauterine Gestation</FindingLabel>
            <BooleanToggle
              value={data.findings.intrauterineGestation}
              onChange={(v) => updateFindings({ intrauterineGestation: v })}
              disabled={readOnly}
            />

            <FindingLabel>Gestational Sac</FindingLabel>
            <BooleanToggle
              value={data.findings.gestationalSacSeen}
              onChange={(v) => updateFindings({ gestationalSacSeen: v })}
              labels={{ yes: "Seen", no: "Not Seen" }}
              disabled={readOnly}
            />

            <FindingLabel>Yolk Sac</FindingLabel>
            <BooleanToggle
              value={data.findings.yolkSacSeen}
              onChange={(v) => updateFindings({ yolkSacSeen: v })}
              labels={{ yes: "Seen", no: "Not Seen" }}
              disabled={readOnly}
            />

            <FindingLabel>Fetal Pole</FindingLabel>
            <BooleanToggle
              value={data.findings.fetalPoleSeen}
              onChange={(v) => updateFindings({ fetalPoleSeen: v })}
              labels={{ yes: "Seen", no: "Not Seen" }}
              disabled={readOnly}
            />

            <FindingLabel>Cardiac Activity</FindingLabel>
            <EnumChipSelect
              options={[...CARDIAC_ACTIVITY_OPTIONS]}
              selected={data.findings.cardiacActivity}
              onChange={(v) =>
                updateFindings({
                  cardiacActivity:
                    v as PregnancyWorksheetData["findings"]["cardiacActivity"],
                })
              }
              disabled={readOnly}
            />

            <NumericInput
              value={data.findings.fetalHeartRateBpm}
              onChange={(v) => updateFindings({ fetalHeartRateBpm: v })}
              unit="bpm"
              step={1}
              label="Fetal Heart Rate"
              disabled={readOnly}
            />

            <NumericInput
              value={data.findings.numberOfGestations}
              onChange={(v) => updateFindings({ numberOfGestations: v })}
              unit=""
              step={1}
              label="Number of Gestations"
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Biometry">
            <NumericInput
              value={data.findings.crownRumpLengthMm}
              onChange={handleCrlChange}
              unit="mm"
              step={0.1}
              label="CRL"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.meanSacDiameterMm}
              onChange={(v) => updateFindings({ meanSacDiameterMm: v })}
              unit="mm"
              step={0.1}
              label="MSD"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.biparietalDiameterMm}
              onChange={(v) => handleBiometryChange("biparietalDiameterMm", v)}
              unit="mm"
              step={0.1}
              label="BPD"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.headCircumferenceMm}
              onChange={(v) => handleBiometryChange("headCircumferenceMm", v)}
              unit="mm"
              step={0.1}
              label="HC"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.abdominalCircumferenceMm}
              onChange={(v) =>
                handleBiometryChange("abdominalCircumferenceMm", v)
              }
              unit="mm"
              step={0.1}
              label="AC"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.femurLengthMm}
              onChange={(v) => handleBiometryChange("femurLengthMm", v)}
              unit="mm"
              step={0.1}
              label="Femur Length"
              disabled={readOnly}
            />
            <FindingLabel>Estimated Fetal Weight (Hadlock)</FindingLabel>
            <NumericInput
              value={data.findings.estimatedFetalWeightG}
              onChange={(v) => updateFindings({ estimatedFetalWeightG: v })}
              unit="g"
              step={1}
              label="EFW"
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Obstetrical">
            <FindingLabel>Fetal Presentation</FindingLabel>
            <EnumChipSelect
              options={[...PRESENTATION_OPTIONS]}
              selected={data.findings.fetalPresentation}
              onChange={(v) =>
                updateFindings({
                  fetalPresentation:
                    v as PregnancyWorksheetData["findings"]["fetalPresentation"],
                })
              }
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.amnioticFluidIndexCm}
              onChange={(v) => updateFindings({ amnioticFluidIndexCm: v })}
              unit="cm"
              step={0.1}
              label="Amniotic Fluid Index"
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Gynecological / Cul-de-sac">
            <FindingLabel>Uterine Fibroids</FindingLabel>
            <BooleanToggle
              value={data.findings.uterineFibroidsSeen}
              onChange={(v) => updateFindings({ uterineFibroidsSeen: v })}
              labels={{ yes: "Seen", no: "Not Seen" }}
              disabled={readOnly}
            />
            <FindingLabel>Pelvic Free Fluid</FindingLabel>
            <EnumChipSelect
              options={[...FREE_FLUID_OPTIONS]}
              selected={data.findings.pelvicFreeFluid}
              onChange={(v) =>
                updateFindings({
                  pelvicFreeFluid:
                    v as PregnancyWorksheetData["findings"]["pelvicFreeFluid"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
